import { SenderPriorityUsageSchema } from "@/api/enums";
import { ChipInput } from "@/components/base";
import { SenderPriorityUsageEnum } from "@/constants";
import type { AuthType } from "@/constants/types";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetAllGroups, useGetAllProxies } from "@/services";
import { getSenderPriorityTranslation } from "@/utils/translations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Checkbox,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
	SmartSelect,
	Typography,
} from "@st-marketplace/ui";
import { t as translate } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const SenderPriorityUsageZod = z.nativeEnum(SenderPriorityUsageEnum);

const common = z.object({
	reserved_balance: z.number().nullable(),
	use_inherited_keep: z.boolean().default(true),
	use_inherited_priority: z.boolean().default(true),
	proxy: z.string(),
	sender_group_name: z.string(),
	email: z.string().email(),
	password: z.string().min(4),
	priority: SenderPriorityUsageZod.nullable().optional(),
});

const gauthVariant = z.object({
	auth_type: z.literal("gauth"),
	// required for gauth
	gauth: z.string().min(1),
	// allowed but not required in this mode
	backup_code: z.array(z.string()).default([]),
	imap_server: z.string().optional(),
	imap_port: z.number().nullable().optional(),
	imap_username: z.string().optional(),
	imap_password: z.string().optional(),
	imap_ssl: z.boolean().optional(),
});

const backupCodeVariant = z.object({
	auth_type: z.literal("backupCode"),
	// required for backup code mode: at least one code
	backup_code: z
		.array(z.string())
		.min(1, translate("validation.backupCodeRequired")),
	// allowed but not required in this mode
	gauth: z.string().optional(),
	imap_server: z.string().optional(),
	imap_port: z.number().nullable().optional(),
	imap_username: z.string().optional(),
	imap_password: z.string().optional(),
	imap_ssl: z.boolean().optional(),
});

const imapVariant = z.object({
	auth_type: z.literal("imap"),
	// all IMAP fields required in this mode
	imap_server: z.string().min(1),
	imap_port: z.number().int().min(1),
	imap_username: z.string().min(1),
	imap_password: z.string().min(1),
	imap_ssl: z.boolean(),
	// allowed but not required in this mode
	gauth: z.string().optional(),
	backup_code: z.array(z.string()).default([]),
});

export const newFormSenderSchema = common
	.and(
		z.discriminatedUnion("auth_type", [
			gauthVariant,
			backupCodeVariant,
			imapVariant,
		]),
	)
	.superRefine((data, ctx) => {
		if (!data.use_inherited_priority && data.priority == null) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["priority"],
				message: translate("validation.required"),
			});
		}

		if (!data.use_inherited_keep && !data.reserved_balance) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["reserved_balance"],
				message: translate("validation.required"),
			});
		}
	});

export type NewFormSenderFormData = z.infer<typeof newFormSenderSchema>;

// Section title with divider so the long form reads as distinct groups
// instead of a wall of inputs.
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
	<div className="col-span-12 mt-2 first:mt-0">
		<Typography variant="caption" className="tracking-widest">
			{title}
		</Typography>
		<hr className="border-secondary-border" />
	</div>
);

const RowRadioButtonsGroup: React.FC<{
	value: AuthType;
	disabled?: boolean;
	onChange: (value: string) => void;
}> = ({ value, onChange, disabled }) => {
	const { t } = useTranslation();

	return (
		<RadioGroup
			value={value}
			onValueChange={onChange}
			orientation="horizontal"
			className="flex flex-row gap-2 md:gap-10"
			disabled={disabled}
		>
			<div className="flex items-center gap-2">
				<RadioGroupItem value="gauth" id="sender-auth-gauth" />
				<Label htmlFor="sender-auth-gauth">
					{t("addSender.form.inputGauth")}
				</Label>
			</div>
			<div className="flex items-center gap-2">
				<RadioGroupItem value="backupCode" id="sender-auth-backup-code" />
				<Label htmlFor="sender-auth-backup-code">
					{t("addSender.form.inputBackupCode")}
				</Label>
			</div>
			<div className="flex items-center gap-2">
				<RadioGroupItem value="imap" id="sender-auth-imap" />
				<Label htmlFor="sender-auth-imap">
					{t("addSender.form.inputImap")}
				</Label>
			</div>
		</RadioGroup>
	);
};

interface NewFormSenderFormProps {
	formData: NewFormSenderFormData;
	loading: boolean;
	onFormChange: (name: keyof NewFormSenderFormData, value: any) => void;
	onSubmit: (data: NewFormSenderFormData) => void;
	onReset: () => void;
}

const NewFormSenderForm: React.FC<NewFormSenderFormProps> = ({
	formData,
	loading,
	onFormChange,
	onSubmit,
	onReset,
}) => {
	const { t } = useTranslation();

	const [codes, setCodes] = useState<string[]>(formData.backup_code);
	const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
	const [isImapPasswordVisible, setIsImapPasswordVisible] =
		useState<boolean>(false);

	const { data: groups } = useGetAllGroups({});
	const { data: proxies } = useGetAllProxies({});

	const form = useForm<NewFormSenderFormData>({
		resolver: zodResolver(newFormSenderSchema),
		values: formData,
		shouldUnregister: false,
	});

	const watchedAuth = form.watch("auth_type");

	const priorityItems = useMemo(
		() =>
			SenderPriorityUsageSchema.anyOf.map((item) => ({
				value: item.const,
				label: getSenderPriorityTranslation(item.const, t).text.toString(),
			})),
		[t],
	);

	const groupItems = useMemo(
		() => (groups ?? []).map((g) => ({ value: g.id, label: g.name })),
		[groups],
	);

	const proxyItems = useMemo(
		() =>
			(proxies ?? []).map((p) => ({
				value: p.id,
				label: `${
					p.address.slice(-1) === "/" ? p.address.slice(0, -1) : p.address
				}:${p.port}`,
			})),
		[proxies],
	);

	const handleFormSubmit = (data: NewFormSenderFormData) => {
		onSubmit(data);
	};

	const handleFieldChange = (name: keyof NewFormSenderFormData, value: any) => {
		form.setValue(name, value, { shouldDirty: true });
		onFormChange(name, value);
	};

	const handleCodeChange = (chips: string[]) => {
		setCodes(chips);
		if (chips.length > 0) {
			handleFieldChange("backup_code", chips);
		} else {
			handleFieldChange("backup_code", []);
		}
	};

	const handleReset = () => {
		form.reset();
		onReset();
	};

	useEffect(() => {
		form.reset(formData);
		setCodes(formData.backup_code ?? []);
	}, [formData, form]);

	const useInheritedPriority = form.watch("use_inherited_priority");
	const useInheritedKeep = form.watch("use_inherited_keep");

	return (
		<Form {...form}>
			<form
				noValidate
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="grid grid-cols-12 gap-4"
			>
				<SectionHeader title={t("addSender.form.sectionAccount")} />

				<div className="col-span-12 md:col-span-6">
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>{t("addSender.form.inputEmail")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										ltr
										placeholder={t("addSender.form.inputEmail")}
										disabled={loading}
										onChange={(e) => handleFieldChange("email", e.target.value)}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-6">
					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("addSender.form.inputPassword")}
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										ltr
										type={isPasswordVisible ? "text" : "password"}
										placeholder={t("addSender.form.inputPassword")}
										disabled={loading}
										onChange={(e) =>
											handleFieldChange("password", e.target.value)
										}
										append={
											<Button
												type="button"
												icon
												variant="text"
												color="ghost"
												size="sm"
												aria-label={
													isPasswordVisible ? "Hide password" : "Show password"
												}
												onClick={() => setIsPasswordVisible(!isPasswordVisible)}
											>
												<Icon
													name={isPasswordVisible ? "eye-off-line" : "eye-line"}
													size="sm"
												/>
											</Button>
										}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<SectionHeader title={t("addSender.form.sectionSettings")} />

				<div className="col-span-12 md:col-span-6">
					<FormField
						control={form.control}
						name="sender_group_name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("addSender.form.inputGroupName")}</FormLabel>
								<FormControl>
									<SmartSelect
										items={groupItems}
										value={field.value || ""}
										onChange={(value) =>
											handleFieldChange("sender_group_name", value)
										}
										searchable
										placeholder={t("addSender.form.inputGroupName")}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-6">
					<FormField
						control={form.control}
						name="proxy"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("addSender.form.inputProxy")}</FormLabel>
								<FormControl>
									<SmartSelect
										items={proxyItems}
										value={field.value || ""}
										onChange={(value) => handleFieldChange("proxy", value)}
										searchable
										placeholder={t("addSender.form.inputProxy")}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-6">
					<Checkbox
						label={t("addSender.form.useInheritedPriority")}
						checked={useInheritedPriority}
						disabled={loading}
						onCheckedChange={(checked) => {
							const next = checked === true;
							handleFieldChange("use_inherited_priority", next);
							if (next) {
								handleFieldChange("priority", null);
							}
						}}
					/>
					<FormDescription>
						{t("addSender.form.inheritedPriorityHelper")}
					</FormDescription>

					{!useInheritedPriority && (
						<FormField
							control={form.control}
							name="priority"
							render={({ field }) => (
								<FormItem className="mt-4">
									<FormLabel>{t("addSender.form.inputPriority")}</FormLabel>
									<FormControl>
										<SmartSelect
											items={priorityItems}
											value={field.value ?? undefined}
											onChange={(value) => handleFieldChange("priority", value)}
											placeholder={t("addSender.form.inputPriority")}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				<div className="col-span-12 md:col-span-6">
					<Checkbox
						label={t("addSender.form.useInheritedKeep")}
						checked={useInheritedKeep}
						disabled={loading}
						onCheckedChange={(checked) => {
							const next = checked === true;
							handleFieldChange("use_inherited_keep", next);
							if (next) {
								handleFieldChange("reserved_balance", null);
							}
						}}
					/>
					<FormDescription>
						{t("addSender.form.inheritedKeepHelper")}
					</FormDescription>

					{!useInheritedKeep && (
						<FormField
							control={form.control}
							name="reserved_balance"
							render={({ field }) => (
								<FormItem className="mt-4">
									<FormLabel>{t("addSender.form.customKeepValue")}</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder={t("addSender.form.customKeepValue")}
											value={field.value ?? ""}
											onChange={(e) =>
												handleFieldChange(
													"reserved_balance",
													e.target.value ? Number(e.target.value) : null,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				<SectionHeader title={t("addSender.form.sectionAuth")} />

				<div className="col-span-12">
					<RowRadioButtonsGroup
						disabled={loading}
						value={watchedAuth}
						onChange={(v) => {
							handleFieldChange("auth_type", v);
						}}
					/>
				</div>

				{/* Reserve a stable minimum height so switching auth type (which
				    renders a different number of fields) doesn't shift the buttons
				    below. IMAP is the tallest variant. */}
				<div className="col-span-12">
					<div className="grid grid-cols-12 gap-4">
						{watchedAuth === "gauth" ? (
							<div className="col-span-12 md:col-span-6 lg:col-span-7">
								<FormField
									control={form.control}
									name="gauth"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t("addSender.form.inputGauth")}</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ""}
													ltr
													placeholder={t("addSender.form.inputGauth")}
													disabled={loading}
													onChange={(e) =>
														handleFieldChange("gauth", e.target.value)
													}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						) : watchedAuth === "backupCode" ? (
							<div className="col-span-12 md:col-span-6 lg:col-span-7">
								<ChipInput
									chips={codes}
									onChipsChange={handleCodeChange}
									label={t("addSender.form.inputBackupCode")}
									disabled={loading}
									error={form.formState.errors.backup_code?.message}
								/>
							</div>
						) : (
							<>
								<div className="col-span-12 md:col-span-6 lg:col-span-4">
									<FormField
										control={form.control}
										name="imap_server"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addSender.form.inputImapServer")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={field.value ?? ""}
														ltr
														placeholder={t("addSender.form.inputImapServer")}
														onChange={(e) =>
															handleFieldChange("imap_server", e.target.value)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 md:col-span-6 lg:col-span-4">
									<FormField
										control={form.control}
										name="imap_port"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addSender.form.inputImapPort")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t("addSender.form.inputImapPort")}
														value={field.value ?? ""}
														onChange={(e) =>
															handleFieldChange(
																"imap_port",
																e.target.value ? Number(e.target.value) : null,
															)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 md:col-span-6 lg:col-span-4">
									<FormField
										control={form.control}
										name="imap_username"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addSender.form.inputImapUsername")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={field.value ?? ""}
														ltr
														placeholder={t("addSender.form.inputImapUsername")}
														onChange={(e) =>
															handleFieldChange("imap_username", e.target.value)
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 md:col-span-6">
									<FormField
										control={form.control}
										name="imap_password"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addSender.form.inputImapPassword")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={field.value ?? ""}
														ltr
														type={isImapPasswordVisible ? "text" : "password"}
														placeholder={t("addSender.form.inputImapPassword")}
														onChange={(e) =>
															handleFieldChange("imap_password", e.target.value)
														}
														append={
															<Button
																type="button"
																icon
																variant="text"
																color="ghost"
																size="sm"
																aria-label={
																	isImapPasswordVisible
																		? "Hide password"
																		: "Show password"
																}
																onClick={() =>
																	setIsImapPasswordVisible(
																		!isImapPasswordVisible,
																	)
																}
															>
																<Icon
																	name={
																		isImapPasswordVisible
																			? "eye-off-line"
																			: "eye-line"
																	}
																	size="sm"
																/>
															</Button>
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 md:col-span-6">
									<FormField
										control={form.control}
										name="imap_ssl"
										render={({ field }) => (
											<FormItem className="mt-1 ms-2">
												<Checkbox
													label={t("addSender.form.inputImapSsl")}
													checked={field.value ?? false}
													onCheckedChange={field.onChange}
												/>
											</FormItem>
										)}
									/>
								</div>
							</>
						)}
					</div>
				</div>

				<div className="col-span-12">
					<hr className="border-secondary-border mb-1" />
				</div>

				<div className="col-span-12">
					<div className="flex justify-end gap-3">
						<Button
							type="button"
							size="lg"
							color="secondary"
							className="min-w-35"
							onClick={handleReset}
						>
							{t("addSender.form.buttonReset")}
						</Button>
						<Button
							size="lg"
							type="submit"
							className="min-w-35"
							loading={loading}
						>
							{t("addSender.form.buttonSubmit")}
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
};

export default NewFormSenderForm;
